import { Form, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/forms/password-input";
import { FormErrors } from "@/components/forms/form-errors";
import { Label } from "@/components/ui/label";
import InputError from "@/components/ui/input-error";
import AuthLayout from "@/layouts/auth_layout";
import { invitationAcceptancePath, logoutPath, signUpPath } from "@/routes";
import type { Role } from "@/types";

type Invitation = {
  code: string;
  email_address: string;
  role: Role;
  company_name: string;
  matches_identity: boolean;
  authenticated: boolean;
};

export default function AcceptInvitation({
  invitation,
}: {
  invitation: Invitation;
}) {
  const { t } = useTranslation();
  return (
    <AuthLayout
      title={t("companies.join_title")}
      description={t("companies.join_description")}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">{invitation.company_name}</h2>
          <p className="text-sm">{t("companies.join_description")}</p>
          <p className="text-sm text-muted-foreground">
            {t("companies.invited_as", {
              role: t(`team.${invitation.role}`),
              email: invitation.email_address,
            })}
          </p>
        </div>
        {!invitation.authenticated || invitation.matches_identity ? (
          <Form
            action={invitationAcceptancePath()}
            method="post"
            disableWhileProcessing
          >
            {({ processing, errors }) => (
              <div className="space-y-4">
                <FormErrors
                  errors={errors}
                  targets={{ password: "invitation-password" }}
                />
                <input type="hidden" name="code" value={invitation.code} />
                {!invitation.authenticated && (
                  <div className="space-y-2">
                    <Label htmlFor="invitation-password">
                      {t("auth.password")}
                    </Label>
                    <PasswordInput
                      id="invitation-password"
                      name="password"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                )}
                <InputError message={Object.values(errors).flat().join(" ")} />
                <Button type="submit" disabled={processing}>
                  {t("companies.accept")}
                </Button>
              </div>
            )}
          </Form>
        ) : (
          <p role="alert" className="text-sm">
            {t("companies.wrong_identity")}
          </p>
        )}
        {invitation.authenticated ? (
          <Link
            href={logoutPath()}
            method="delete"
            as="button"
            className="text-sm underline"
          >
            {t("common.sign_out")}
          </Link>
        ) : (
          <Link
            href={signUpPath({ invitation_code: invitation.code })}
            className="text-sm underline"
          >
            {t("companies.new_identity")}
          </Link>
        )}
      </div>
    </AuthLayout>
  );
}
